import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { Home, Users, Shield, ShoppingCart, DollarSign, TrendingUp, BarChart3, FileText, Settings, Zap, Target, Activity, ChevronDown, LineChart, Megaphone, Star, Search, Palette, CreditCard, ShieldCheck, TrendingDown, PieChart, Database, UserCog, Upload } from 'lucide-react';
import { Sidebar, SidebarContent, SidebarHeader, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useEffectiveIdentity } from '@/hooks/useEffectiveIdentity';
import { AdminImpersonationModal } from '@/components/AdminImpersonationModal';

import { Button } from '@/components/ui/button';
export function AppSidebar() {
  const {
    state
  } = useSidebar();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentPath = location.pathname;
  const collapsed = state === "collapsed";
  const [executiveSummaryOpen, setExecutiveSummaryOpen] = useState(true);
  const [taskManagerOpen, setTaskManagerOpen] = useState(true);
  const [labsOpen, setLabsOpen] = useState(true);
  const [monetizationOpen, setMonetizationOpen] = useState(true);
  const [databaseOpen, setDatabaseOpen] = useState(true);
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false);
  
  
  // Get user role and permissions - CRITICAL: Use effectiveIdentity for impersonation
  const identity = useEffectiveIdentity();
  const { 
    isAdmin, 
    isProcurementManager, 
    isProviderManager, 
    isQAManager, 
    isMAManager, 
    isTechApps, 
    isTechInfrastructure, 
    isCreativeManager, 
    canAccessLab, 
    role, 
    loading, 
    allowedPages, 
    allowedLabs,
    department, 
    effectiveRole, 
    effectiveDepartment, 
    impersonationMode,
    isImpersonating
  } = identity;
  
  console.log('[AppSidebar] Current effective identity:', { 
    role, 
    department, 
    isAdmin, 
    effectiveRole, 
    effectiveDepartment, 
    impersonationMode, 
    isImpersonating,
    allowedPages 
  });
  
  // Derive pages strictly from the effective role/department while impersonating
  const derivedAllowedPages = (() => {
    if (!isImpersonating) return allowedPages;

    if (effectiveRole === 'admin') {
      return [
        'all',
        '/operational-summary',
        '/performance-summary',
        '/overview',
        '/timeline-dashboard',
        '/task-manager',
        '/negotiation-manager',
        '/app-database',
        '/creative-database',
        '/providers-database',
        '/admin',
        '/aso',
        '/product',
        '/usability',
        '/compliance',
        '/ad-monetization',
        '/user-acquisition',
        '/usability-performance-summary'
      ];
    }

    if (effectiveRole === 'procurement_manager') {
      return ['/task-manager', '/app-database', '/creative-database', '/providers-database'];
    }
    if (effectiveRole === 'provider_manager') {
      return ['/task-manager'];
    }
    if (effectiveRole === 'qa_manager') {
      return ['/app-database', '/task-manager'];
    }
    if (effectiveRole === 'ma_manager') {
      return ['/app-database', '/task-manager', '/negotiation-manager'];
    }
    if (effectiveRole === 'tech_apps' || effectiveRole === 'tech_infrastructure') {
      return ['/app-database', '/task-manager'];
    }
    if (effectiveRole === 'creative_manager') {
      return ['/app-database', '/task-manager'];
    }

    if (effectiveRole === 'department_manager') {
      const pages = ['/task-manager', '/app-database', '/performance-summary'];
      if (effectiveDepartment === 'aso') pages.push('/aso');
      if (effectiveDepartment === 'product') pages.push('/product', '/usability', '/usability-performance-summary');
      if (effectiveDepartment === 'compliance') pages.push('/compliance');
      if (effectiveDepartment === 'ad_monetization') pages.push('/ad-monetization');
      if (effectiveDepartment === 'user_acquisition') pages.push('/user-acquisition');
      return pages;
    }

    return [];
  })();
  
  // Check if user is a department manager (using effective role for impersonation)
  const isDepartmentManager = effectiveRole === 'department_manager';
  
  // Check if user is Product Manager (using effective department for impersonation)
  const isProductManager = effectiveRole === 'department_manager' && effectiveDepartment === 'product';
  
  // Check if user can access a page (uses derived pages during impersonation)
  const canAccessPage = (pagePath: string): boolean => {
    if (loading) return false;
    const pages = isImpersonating ? derivedAllowedPages : allowedPages;
    if (pages.includes('all')) return true;
    return pages.includes(pagePath);
  };
  // Get current app from URL params
  const currentApp = searchParams.get('app');
  
  // Helper to build URL with app param
  const buildUrl = (path: string) => {
    return currentApp ? `${path}?app=${currentApp}` : path;
  };
  
  const isActive = (path: string) => currentPath === path;
  const getSectionStyle = (isOpen: boolean) => `flex items-center justify-between w-full px-3 py-2.5 text-left font-bold text-gray-900 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer ${!collapsed ? 'text-sm' : 'text-xs'}`;
  const getNavItemStyle = (active: boolean, indented = true) => `flex items-center w-full px-3 py-2 text-xs hover:bg-gray-50 rounded-lg transition-colors ${active ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'} ${indented && !collapsed ? 'ml-4' : ''}`;
  const getIconClass = (active: boolean, defaultColor?: string) => `h-3 w-3 mr-2 ${active ? 'text-purple-700' : defaultColor || ''}`;
  return <Sidebar className={collapsed ? "w-14" : "w-64"} collapsible="icon">
      <SidebarHeader className="border-b border-gray-200 bg-white">
        <div className="px-4 py-[7px] flex items-center justify-center">
          <div>
            {!collapsed && <h1 className="text-xl font-bold">
                <span className="text-orange-500">App</span>
                <span className="text-gray-900">Remedy</span>
              </h1>}
            {collapsed && <div className="text-lg font-bold text-orange-500">
                AR
              </div>}
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-2">
        {/* Executive Summary - Only show if user can access at least one summary page */}
        {(canAccessPage('/operational-summary') || canAccessPage('/performance-summary') || canAccessPage('/usability-performance-summary')) && (
          <Collapsible open={executiveSummaryOpen} onOpenChange={setExecutiveSummaryOpen}>
            <div className="mb-0">
              <CollapsibleTrigger className={getSectionStyle(executiveSummaryOpen)}>
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-3 w-3 text-green-600" />
                  {!collapsed && <span>Executive Summary</span>}
                </div>
                {!collapsed && <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${executiveSummaryOpen ? 'rotate-180' : ''}`} />}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 space-y-1">
                  {/* Operational Summary - Only if allowed */}
                  {canAccessPage('/operational-summary') && (
                    <NavLink to="/operational-summary" className={getNavItemStyle(isActive('/operational-summary'))}>
                      <Activity className={getIconClass(isActive('/operational-summary'), 'text-red-600')} />
                      {!collapsed && <span className={isActive('/operational-summary') ? '' : 'text-red-600'}>Operational Summary</span>}
                    </NavLink>
                  )}
                  
                  {/* Performance Summary - Only if allowed */}
                  {canAccessPage('/performance-summary') && (
                    <NavLink to="/performance-summary" className={getNavItemStyle(isActive('/performance-summary'))}>
                      <TrendingUp className={getIconClass(isActive('/performance-summary'))} />
                      {!collapsed && <span>{isProductManager ? 'Product Portfolio Summary' : 'Portfolio Summary'}</span>}
                    </NavLink>
                  )}
                  
                  {/* Usability Performance Summary - Only if allowed */}
                  {canAccessPage('/usability-performance-summary') && (
                    <NavLink to="/usability-performance-summary" className={getNavItemStyle(isActive('/usability-performance-summary'))}>
                      <Users className={getIconClass(isActive('/usability-performance-summary'))} />
                      {!collapsed && <span>Usability Portfolio Summary</span>}
                    </NavLink>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}
        
        {/* Task Manager - Show only if user has access */}
        {canAccessPage('/task-manager') && (
          <Collapsible open={taskManagerOpen} onOpenChange={setTaskManagerOpen}>
            <div className="mb-0">
              <CollapsibleTrigger className={getSectionStyle(taskManagerOpen)}>
                <div className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 bg-purple-600 rounded-full"></div>
                  {!collapsed && <span>{isMAManager ? 'Verification' : 'Task Manager'}</span>}
                </div>
                {!collapsed && <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${taskManagerOpen ? 'rotate-180' : ''}`} />}
              </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-1">
                {canAccessPage('/task-manager') && (
                  <NavLink to="/task-manager" className={getNavItemStyle(isActive('/task-manager'))}>
                    <Target className={getIconClass(isActive('/task-manager'))} />
                    {!collapsed && <span>{isMAManager ? 'Verification Manager' : 'Task Manager'}</span>}
                  </NavLink>
                )}
                {canAccessPage('/negotiation-manager') && (
                  <NavLink to="/negotiation-manager" className={getNavItemStyle(isActive('/negotiation-manager'))}>
                    <Target className={getIconClass(isActive('/negotiation-manager'))} />
                    {!collapsed && <span>Negotiation Manager</span>}
                  </NavLink>
                )}
              </div>
            </CollapsibleContent>
            </div>
          </Collapsible>
        )}
        
        {/* Performance Labs - Show for department managers and admins only who can access labs */}
        {!loading && (isDepartmentManager || isAdmin) && !isCreativeManager && allowedLabs.length > 0 && (
          <Collapsible open={labsOpen} onOpenChange={setLabsOpen}>
            <div className="mb-0">
              <CollapsibleTrigger className={getSectionStyle(labsOpen)}>
                <div className="flex items-center space-x-2">
                  <Zap className="h-3 w-3 text-yellow-600" />
                  {!collapsed && <span>Performance Labs</span>}
                </div>
                {!collapsed && <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${labsOpen ? 'rotate-180' : ''}`} />}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 space-y-1">
                  {/* Score Summary only for admins */}
                  {canAccessPage('/overview') && (
                    <NavLink to={buildUrl("/overview")} className={getNavItemStyle(isActive('/overview'))}>
                      <PieChart className={getIconClass(isActive('/overview'))} />
                      {!collapsed && <span>Score Summary</span>}
                    </NavLink>
                  )}
                  
                  {/* Store Optimization / ASO */}
                  {canAccessPage('/aso') && (
                    <NavLink to={buildUrl("/aso")} className={getNavItemStyle(isActive('/aso'))}>
                      <Search className={getIconClass(isActive('/aso'))} />
                      {!collapsed && <span>Store Optimization</span>}
                    </NavLink>
                  )}
                  
                  {/* Product */}
                  {canAccessPage('/product') && (
                    <NavLink to={buildUrl("/product")} className={getNavItemStyle(isActive('/product'))}>
                      <Star className={getIconClass(isActive('/product'))} />
                      {!collapsed && <span>Product</span>}
                    </NavLink>
                  )}
                  
                  {/* Usability */}
                  {canAccessPage('/usability') && (
                    <NavLink to={buildUrl("/usability")} className={getNavItemStyle(isActive('/usability'))}>
                      <Users className={getIconClass(isActive('/usability'))} />
                      {!collapsed && <span>Usability</span>}
                    </NavLink>
                  )}
                  
                  {/* Compliance */}
                  {canAccessPage('/compliance') && (
                    <NavLink to={buildUrl("/compliance")} className={getNavItemStyle(isActive('/compliance'))}>
                      <ShieldCheck className={getIconClass(isActive('/compliance'))} />
                      {!collapsed && <span>Compliance</span>}
                    </NavLink>
                  )}
                  
                  {/* Ad Monetization */}
                  {canAccessPage('/ad-monetization') && (
                    <NavLink to={buildUrl("/ad-monetization")} className={getNavItemStyle(isActive('/ad-monetization'))}>
                      <DollarSign className={getIconClass(isActive('/ad-monetization'))} />
                      {!collapsed && <span>Ad Monetization</span>}
                    </NavLink>
                  )}
                  
                  {/* User Acquisition */}
                  {canAccessPage('/user-acquisition') && (
                    <NavLink to={buildUrl("/user-acquisition")} className={getNavItemStyle(isActive('/user-acquisition'))}>
                      <Megaphone className={getIconClass(isActive('/user-acquisition'))} />
                      {!collapsed && <span>User Acquisition</span>}
                    </NavLink>
                  )}
                  
                  {/* Timeline only for admins */}
                  {canAccessPage('/timeline-dashboard') && (
                    <NavLink to={buildUrl("/timeline-dashboard")} className={getNavItemStyle(isActive('/timeline-dashboard'))}>
                      <LineChart className={getIconClass(isActive('/timeline-dashboard'), 'text-red-600')} />
                      {!collapsed && <span className={isActive('/timeline-dashboard') ? '' : 'text-red-600'}>Timeline</span>}
                    </NavLink>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}

        {/* Database - Show only if user has access to at least one database page */}
        {(canAccessPage('/app-database') || canAccessPage('/creative-database') || canAccessPage('/providers-database')) && (
          <Collapsible open={databaseOpen} onOpenChange={setDatabaseOpen}>
            <div className="mb-0">
              <CollapsibleTrigger className={getSectionStyle(databaseOpen)}>
                <div className="flex items-center space-x-2">
                  <Database className="h-3 w-3 text-blue-600" />
                  {!collapsed && <span>{isMAManager ? 'Apps' : 'Database'}</span>}
                </div>
                {!collapsed && <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${databaseOpen ? 'rotate-180' : ''}`} />}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 space-y-1">
                  {canAccessPage('/app-database') && (
                    <NavLink to="/app-database" className={getNavItemStyle(isActive('/app-database'))}>
                      <Database className={getIconClass(isActive('/app-database'))} />
                      {!collapsed && <span>{isMAManager ? 'Apps Database' : 'App Database'}</span>}
                    </NavLink>
                  )}
                  {canAccessPage('/creative-database') && (
                    <NavLink to="/creative-database" className={getNavItemStyle(isActive('/creative-database'))}>
                      <Palette className={getIconClass(isActive('/creative-database'))} />
                      {!collapsed && <span>Creative Database</span>}
                    </NavLink>
                  )}
                  {canAccessPage('/providers-database') && (
                    <NavLink to="/providers-database" className={getNavItemStyle(isActive('/providers-database'))}>
                      <Users className={getIconClass(isActive('/providers-database'))} />
                      {!collapsed && <span>Providers Database</span>}
                    </NavLink>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}

        {/* Admin Section - Only for REAL admins (not impersonated) */}
        {!loading && !isImpersonating && isAdmin && (
          <div className="mt-auto pt-4 border-t border-gray-200 space-y-1">
            <NavLink to={buildUrl("/admin")} className={getNavItemStyle(isActive('/admin'), false)}>
              <Settings className={getIconClass(isActive('/admin'))} />
              {!collapsed && <span>Admin</span>}
            </NavLink>
            <Button
              onClick={() => setImpersonationModalOpen(true)}
              variant="ghost"
              className={`w-full justify-start ${collapsed ? 'px-2' : 'px-3'} py-2 text-xs hover:bg-purple-50 hover:text-purple-700 transition-colors`}
            >
              <UserCog className="h-3 w-3 mr-2 text-purple-600" />
              {!collapsed && <span>Impersonation</span>}
            </Button>
          </div>
        )}
      </SidebarContent>

      {/* Admin Impersonation Modal - Only for REAL admins */}
      {!loading && !isImpersonating && isAdmin && (
        <>
          <AdminImpersonationModal
            open={impersonationModalOpen}
            onOpenChange={setImpersonationModalOpen}
          />
        </>
      )}
    </Sidebar>;
}